// Risk Assessment Engine
// Comprehensive risk calculation combining multiple factors
// Phase 4 Week 2 & 4

import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { patientRiskFactorService } from './patientRiskFactorService';
import { environmentalRiskFactorService } from './environmentalRiskFactorService';
import { riskAlertManager } from './riskAlertManager';
import { biologicalEntityEmergenceCalculator } from './biologicalEntityEmergenceCalculator';
import { biologicalEntityService } from './firebaseService';

/**
 * Risk Assessment Engine
 * Calculates comprehensive risk scores using multiplicative factors
 */
export class RiskAssessmentEngine {
  
  /**
   * Main calculation method - combines all risk factors
   * 
   * @param {Object} baseRisk - Risk from protocol_risks collection
   * @param {Object} context - Assessment context
   * @returns {Object} Comprehensive risk assessment
   */
  async calculateComprehensiveRisk(baseRisk, context) {
    const {
      patient = null,
      department = null,
      room = null,
      bed = null,
      location = null,
      environmentalConditions = null,
      biological_entities = []
    } = context;
    
    // Start with base RPN from protocol risk
    const baseRPN = baseRisk.rpn || baseRisk.calculatedRPN || 0;
    let finalScore = baseRPN;
    
    // Track all applied factors
    const appliedFactors = {
      location: 1.0,
      patient: 1.0,
      environmental: 1.0,
      biological: 1.0,
      euDisease: 1.0 // NEW: EU disease multiplier
    };
    
    const appliedDetails = {
      patientRiskFactorIds: [],
      environmentalFactorIds: [],
      biologicalEntityIds: [],
      locationInfo: {},
      euDiseases: [], // NEW: Detected EU diseases
      euReportingRequired: false // NEW: EU reporting flag
    };
    
    // 1. Calculate Location-based multiplier
    if (department || room || location) {
      const { factor, details } = await this.calculateLocationFactor(
        location || department, 
        department, 
        room, 
        baseRisk
      );
      appliedFactors.location = factor;
      appliedDetails.locationInfo = details;
      finalScore *= factor;
    }
    
    // 2. Calculate Patient-based multiplier
    if (patient) {
      const { factor, appliedRiskFactors, details } = await this.calculatePatientFactor(
        patient, 
        baseRisk
      );
      appliedFactors.patient = factor;
      appliedDetails.patientRiskFactorIds = appliedRiskFactors;
      appliedDetails.patientDetails = details;
      finalScore *= factor;
    }
    
    // 3. Calculate Environmental multiplier
    if (environmentalConditions && department) {
      const { factor, appliedFactorIds, details } = await this.calculateEnvironmentalFactor(
        environmentalConditions,
        department,
        baseRisk
      );
      appliedFactors.environmental = factor;
      appliedDetails.environmentalFactorIds = appliedFactorIds;
      appliedDetails.environmentalDetails = details;
      finalScore *= factor;
    }
    
    // 4. Calculate Biological entity multiplier
    if (biological_entities && biological_entities.length > 0 && baseRisk.biological_entities) {
      const { factor, matchingEntityIds, euMultiplier, euDiseases, euReportingRequired } = await this.calculateBiologicalFactorWithEU(
        biological_entities,
        baseRisk.biological_entities,
        context
      );
      appliedFactors.biological = factor;
      appliedDetails.biologicalEntityIds = matchingEntityIds;
      finalScore *= factor;
      
      // NEW: Apply EU disease multiplier if EU notifiable disease detected
      if (euMultiplier > 1.0) {
        appliedFactors.euDisease = euMultiplier;
        appliedDetails.euDiseases = euDiseases || [];
        appliedDetails.euReportingRequired = euReportingRequired || false;
        finalScore *= euMultiplier;
      }
    }
    
    // Round final score
    finalScore = Math.round(finalScore);
    
    // Determine risk level
    const riskLevel = this.determineRiskLevel(finalScore);
    
    // Generate recommendations
    const recommendations = this.generateRecommendations(
      finalScore, 
      riskLevel, 
      context, 
      appliedFactors
    );
    
    const assessment = {
      baseRPN,
      finalRiskScore: finalScore, // Alias for consistency with alert manager
      finalScore,
      riskLevel,
      multipliers: appliedFactors, // Alias for alert manager
      factors: appliedFactors,
      appliedDetails,
      recommendations,
      calculatedAt: new Date()
    };
    
    // NEW: Calculate biological entity emergence probabilities
    try {
      console.log('🧬 Calculating biological entity emergence probabilities...');
      const emergenceProbabilities = await this.calculateBiologicalEntityEmergenceRisks(context);
      assessment.emergenceProbabilities = emergenceProbabilities;
      console.log('✅ Emergence probabilities calculated:', emergenceProbabilities.length, 'entities');
    } catch (error) {
      console.error('Error calculating emergence probabilities:', error);
      assessment.emergenceProbabilities = [];
    }
    
    // Week 4: Trigger alerts if risk thresholds are exceeded
    // Don't await - let alerts process asynchronously
    try {
      console.log('🚨 RiskAssessmentEngine: Attempting to trigger alerts for assessment:', assessment);
      console.log('🚨 Context:', context);
      riskAlertManager.checkRiskThresholds(assessment, context).catch(err => {
        console.error('Error triggering risk alerts:', err);
      });
    } catch (error) {
      console.error('Error in alert trigger:', error);
    }
    
    return assessment;
  }

  /**
   * Calculate biological entity emergence probabilities
   * NEW: Prevention-focused risk assessment
   * 
   * @param {Object} context - Assessment context
   * @returns {Array} Array of emergence probability results
   */
  async calculateBiologicalEntityEmergenceRisks(context) {
    try {
      // Get all biological entities
      const entities = await biologicalEntityService.getAll();
      
      if (!entities || entities.length === 0) {
        console.log('⚠️ No biological entities found for emergence calculation');
        return [];
      }

      // Get patient risk factors
      const patientRiskFactors = context.patient?.riskFactorIds ? 
        await this.getPatientRiskFactors(context.patient.riskFactorIds) : [];

      // Get environmental risk factors (mock for now - will be enhanced in Phase 6)
      const environmentalRiskFactors = await this.getMockEnvironmentalRiskFactors(context);

      // Get protocol compliance data (mock for now - will be enhanced in Phase 6)
      const protocols = await this.getMockProtocolCompliance(context);

      // Prepare context for emergence calculator
      const emergenceContext = {
        patientRiskFactors: patientRiskFactors,
        environmentalRiskFactors: environmentalRiskFactors,
        locationType: context.department?.type || 'Medical Ward',
        protocols: protocols,
        season: this.getCurrentSeason()
      };

      // Calculate emergence probabilities for all entities
      const emergenceProbabilities = biologicalEntityEmergenceCalculator
        .calculateMultipleEmergenceProbabilities(entities, emergenceContext);

      // Filter to only significant risks (above minimum threshold)
      const significantRisks = emergenceProbabilities.filter(result => 
        result.emergenceProbability >= 0.25 // 25% minimum threshold
      );

      console.log(`🧬 Found ${significantRisks.length} entities with significant emergence risk`);
      
      return significantRisks;

    } catch (error) {
      console.error('Error calculating biological entity emergence risks:', error);
      return [];
    }
  }

  /**
   * Get patient risk factors by IDs
   */
  async getPatientRiskFactors(riskFactorIds) {
    try {
      const factors = await patientRiskFactorService.getMultiple(riskFactorIds);
      return factors.map(factor => ({
        id: factor.id,
        name: factor.name,
        category: factor.category,
        multiplier: factor.multiplier
      }));
    } catch (error) {
      console.error('Error getting patient risk factors:', error);
      return [];
    }
  }

  /**
   * Get mock environmental risk factors (Phase 6 will enhance this)
   */
  async getMockEnvironmentalRiskFactors(context) {
    // Mock environmental risk factors - will be replaced with real data in Phase 6
    const mockFactors = [
      { id: 'mock-1', name: 'Poor ventilation', category: 'Environmental' },
      { id: 'mock-2', name: 'Inadequate cleaning', category: 'Environmental' },
      { id: 'mock-3', name: 'High humidity', category: 'Environmental' }
    ];

    // Simulate some environmental risks based on location
    if (context.department?.type === 'ICU') {
      return [mockFactors[0], mockFactors[1]]; // ICU has poor ventilation and inadequate cleaning
    }
    
    return [mockFactors[2]]; // Other locations have high humidity
  }

  /**
   * Get mock protocol compliance data (Phase 6 will enhance this)
   */
  async getMockProtocolCompliance(context) {
    // Mock protocol compliance - will be replaced with real data in Phase 6
    return [
      {
        id: 'hand-hygiene',
        name: 'Hand hygiene',
        complianceRate: 0.85, // 85% compliance
        lastUpdated: new Date()
      },
      {
        id: 'contact-precautions',
        name: 'Contact precautions',
        complianceRate: 0.70, // 70% compliance
        lastUpdated: new Date()
      },
      {
        id: 'environmental-cleaning',
        name: 'Environmental cleaning',
        complianceRate: 0.80, // 80% compliance
        lastUpdated: new Date()
      }
    ];
  }

  /**
   * Get current season (simplified)
   */
  getCurrentSeason() {
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'fall';
    return 'winter';
  }
  
  /**
   * Calculate location-based risk multiplier
   * Based on department type, room characteristics, isolation capability
   */
  async calculateLocationFactor(location, department, room, risk) {
    let factor = 1.0;
    const details = {
      departmentImpact: 0,
      roomImpact: 0,
      isolationImpact: 0
    };
    
    // Department-specific multipliers
    if (department) {
      // High-risk departments
      const highRiskDepartments = ['ICU', 'Surgery', 'Emergency', 'Operating Room'];
      const deptType = department.type || department.medicalSpecialty || '';
      
      if (highRiskDepartments.some(type => deptType.includes(type))) {
        const deptMultiplier = 1.3;
        factor *= deptMultiplier;
        details.departmentImpact = deptMultiplier;
      }
      
      // Check if risk explicitly applies to this department
      if (risk.applicableDepartments && 
          risk.applicableDepartments.includes(department.id)) {
        const applicableMultiplier = 1.2;
        factor *= applicableMultiplier;
        details.departmentImpact *= applicableMultiplier;
      }
    }
    
    // Room-specific multipliers
    if (room) {
      // Isolation capability
      if (!room.isolationCapability || room.isolationCapability === 'No') {
        if (risk.riskType === 'Infection Transmission' || 
            risk.category === 'Clinical') {
          const isolationMultiplier = 1.4;
          factor *= isolationMultiplier;
          details.isolationImpact = isolationMultiplier;
        }
      }
      
      // Shared rooms (multiple beds)
      const bedCount = room.bedCount || room.numberOfBeds || 1;
      if (bedCount > 1) {
        const sharedMultiplier = 1.2;
        factor *= sharedMultiplier;
        details.roomImpact = sharedMultiplier;
      }
      
      // Poor ventilation
      if (room.airChangesPerHour && room.airChangesPerHour < 6) {
        const ventilationMultiplier = 1.3;
        factor *= ventilationMultiplier;
        details.roomImpact *= ventilationMultiplier;
      }
    }
    
    // Cap location factor at 2.0
    factor = Math.min(factor, 2.0);
    
    return { factor, details };
  }
  
  /**
   * Calculate patient-based risk multiplier
   * Uses patient's assigned risk factors from Week 1
   */
  async calculatePatientFactor(patient, risk) {
    let factor = 1.0;
    const appliedRiskFactors = [];
    const details = {
      factorsApplied: [],
      individualMultipliers: []
    };
    
    // Get patient's risk factors
    if (!patient.riskFactorIds || patient.riskFactorIds.length === 0) {
      return { factor: 1.0, appliedRiskFactors: [], details };
    }
    
    try {
      // Load all patient's risk factors from Firebase
      const patientFactors = await patientRiskFactorService.getFactorsByIds(
        patient.riskFactorIds
      );
      
      // Apply each risk factor's multiplier
      for (const riskFactor of patientFactors) {
        if (!riskFactor || !riskFactor.isActive) continue;
        
        // Check if factor is applicable
        const isApplicable = this.isPatientFactorApplicable(riskFactor, patient);
        
        if (isApplicable) {
          factor *= riskFactor.multiplier;
          appliedRiskFactors.push(riskFactor.id);
          details.factorsApplied.push({
            id: riskFactor.id,
            name: riskFactor.name,
            multiplier: riskFactor.multiplier,
            category: riskFactor.category
          });
          details.individualMultipliers.push(riskFactor.multiplier);
        }
      }
      
    } catch (error) {
      console.error('Error calculating patient factors:', error);
    }
    
    // Cap patient factor at 3.0
    factor = Math.min(factor, 3.0);
    
    return { factor, appliedRiskFactors, details };
  }
  
  /**
   * Check if patient risk factor is applicable
   */
  isPatientFactorApplicable(riskFactor, patient) {
    // Check age range if specified
    if (riskFactor.ageRange) {
      const patientAge = patient.ageRange;
      
      // Simple age range matching
      if (riskFactor.ageRange.min && patientAge === '<18') {
        // Factor requires minimum age
        return false;
      }
      
      if (riskFactor.ageRange.max && riskFactor.ageRange.max < 18 && patientAge !== '<18') {
        // Factor only applies to children
        return false;
      }
      
      if (riskFactor.ageRange.min && riskFactor.ageRange.min >= 65 && patientAge !== '>65') {
        // Factor only applies to elderly
        return false;
      }
    }
    
    // Check applicable conditions if specified
    if (riskFactor.applicableConditions && 
        riskFactor.applicableConditions.length > 0 &&
        patient.conditions &&
        patient.conditions.length > 0) {
      
      // Check if patient has any of the applicable conditions
      const hasCondition = riskFactor.applicableConditions.some(condition =>
        patient.conditions.some(patientCondition => 
          patientCondition.toLowerCase().includes(condition.toLowerCase())
        )
      );
      
      // If factor has specific conditions but patient doesn't have them, 
      // still apply but with reduced confidence
      if (!hasCondition) {
        // For now, still apply the factor
        // In future, could reduce multiplier
      }
    }
    
    // Factor is applicable
    return true;
  }
  
  /**
   * Calculate environmental risk multiplier
   * Uses environmental factors from Week 1 library
   */
  async calculateEnvironmentalFactor(environmentalConditions, department, risk) {
    let factor = 1.0;
    const appliedFactorIds = [];
    const details = {
      factorsApplied: [],
      individualMultipliers: []
    };
    
    // Get active environmental factor IDs from conditions
    const activeFactorIds = environmentalConditions.activeFactorIds || 
                           environmentalConditions.environmentalFactorIds || 
                           [];
    
    if (activeFactorIds.length === 0) {
      return { factor: 1.0, appliedFactorIds: [], details };
    }
    
    try {
      // Load environmental factors from Firebase
      const envFactors = await environmentalRiskFactorService.getFactorsByIds(
        activeFactorIds
      );
      
      // Apply each environmental factor's multiplier
      for (const envFactor of envFactors) {
        if (!envFactor || !envFactor.isActive) continue;
        
        // Check if factor applies to this department
        const isApplicable = this.isEnvironmentalFactorApplicable(
          envFactor, 
          department
        );
        
        if (isApplicable) {
          factor *= envFactor.multiplier;
          appliedFactorIds.push(envFactor.id);
          details.factorsApplied.push({
            id: envFactor.id,
            name: envFactor.name,
            multiplier: envFactor.multiplier,
            category: envFactor.category
          });
          details.individualMultipliers.push(envFactor.multiplier);
        }
      }
      
    } catch (error) {
      console.error('Error calculating environmental factors:', error);
    }
    
    // Cap environmental factor at 2.0
    factor = Math.min(factor, 2.0);
    
    return { factor, appliedFactorIds, details };
  }
  
  /**
   * Check if environmental factor is applicable to department
   */
  isEnvironmentalFactorApplicable(envFactor, department) {
    // If factor is department-specific, check if it applies
    if (envFactor.departmentSpecific && 
        envFactor.departmentSpecific.length > 0) {
      return envFactor.departmentSpecific.includes(department.id);
    }
    
    // Factor applies to all departments
    return true;
  }
  
  /**
   * Calculate biological entity interaction multiplier
   * Uses biological entities from existing system
   */
  calculateBiologicalFactor(contextEntities, riskEntities) {
    let factor = 1.0;
    const matchingEntityIds = [];
    
    // Find biological entities that match between context and risk
    const matchingEntities = contextEntities.filter(entity => {
      const isMatch = riskEntities.some(re => 
        re.biologicalEntityId === entity.id
      );
      
      if (isMatch) {
        matchingEntityIds.push(entity.id);
      }
      
      return isMatch;
    });
    
    if (matchingEntities.length > 0) {
      // Multiple biological entities increase risk
      // Each entity adds 20% to the multiplier
      factor *= (1 + (matchingEntities.length * 0.2));
    }
    
    // Cap biological factor at 2.0
    factor = Math.min(factor, 2.0);
    
    return { factor, matchingEntityIds };
  }

  /**
   * Calculate Biological entity multiplier with EU disease support
   * NEW: Part of Policy Framework & EU Diseases Implementation
   */
  async calculateBiologicalFactorWithEU(contextEntities, riskEntities, context) {
    try {
      // First calculate standard biological factor
      const { factor, matchingEntityIds } = this.calculateBiologicalFactor(
        contextEntities,
        riskEntities
      );

      // NEW: Check for EU notifiable diseases
      let euMultiplier = 1.0;
      const euDiseases = [];
      let euReportingRequired = false;

      if (contextEntities && contextEntities.length > 0) {
        // Load full entity data from Firestore to check EU fields
        // Use existing biologicalEntityService import from top of file
        for (const entityId of matchingEntityIds) {
          try {
            const fullEntity = await biologicalEntityService.getById(entityId);
            
            if (!fullEntity) {
              console.warn(`Entity ${entityId} not found`);
              continue;
            }
            
            if (fullEntity && fullEntity.euNotifiable) {
              // EU notifiable disease detected
              const euRpnMultiplier = fullEntity.euRpnMultiplier || 1.0;
              
              // Use highest EU multiplier if multiple EU diseases
              if (euRpnMultiplier > euMultiplier) {
                euMultiplier = euRpnMultiplier;
              }
              
              euDiseases.push({
                id: fullEntity.id,
                name: fullEntity.commonName || fullEntity.scientificName,
                euDiseaseCode: fullEntity.euDiseaseCode,
                ecdcCode: fullEntity.ecdcCode,
                euRpnMultiplier: euRpnMultiplier,
                euReportingDeadline: fullEntity.euReportingDeadline,
                euIsolationRequired: fullEntity.euIsolationRequired,
                euContactTracingRequired: fullEntity.euContactTracingRequired
              });
              
              euReportingRequired = true;
              
              // Check for outbreak threshold
              if (fullEntity.euOutbreakThreshold && context.locationId) {
                const diseaseOccurrences = await this.countDiseaseOccurrences(
                  context.locationId,
                  fullEntity.id,
                  '7days'
                );
                
                if (diseaseOccurrences >= fullEntity.euOutbreakThreshold) {
                  // Apply alert threshold multiplier for outbreak
                  const alertMultiplier = fullEntity.euAlertThresholdMultiplier || 1.0;
                  euMultiplier *= alertMultiplier;
                  
                  // Trigger EU outbreak alert
                  await this.triggerEUOutbreakAlert(context.locationId, fullEntity);
                }
              }
            }
          } catch (error) {
            console.error(`Error checking EU status for entity ${entityId}:`, error);
          }
        }
      }

      return {
        factor,
        matchingEntityIds,
        euMultiplier: euMultiplier > 1.0 ? euMultiplier : 1.0,
        euDiseases,
        euReportingRequired
      };
    } catch (error) {
      console.error('Error calculating biological factor with EU:', error);
      // Fallback to standard calculation
      const { factor, matchingEntityIds } = this.calculateBiologicalFactor(
        contextEntities,
        riskEntities
      );
      return {
        factor,
        matchingEntityIds,
        euMultiplier: 1.0,
        euDiseases: [],
        euReportingRequired: false
      };
    }
  }

  /**
   * Count disease occurrences in location
   * 
   * @param {string} locationId - Location ID (department, room, etc.)
   * @param {string} diseaseId - Biological entity ID (disease)
   * @param {string} timeframe - Timeframe: '7days', '30days', '90days'
   * @returns {Promise<number>} Count of occurrences
   */
  async countDiseaseOccurrences(locationId, diseaseId, timeframe) {
    try {
      if (!locationId || !diseaseId || !timeframe) {
        console.warn('Missing parameters for countDiseaseOccurrences:', { locationId, diseaseId, timeframe });
        return 0;
      }

      // Calculate date range based on timeframe
      const now = new Date();
      let daysAgo = 7; // Default to 7 days
      
      switch (timeframe) {
        case '7days':
          daysAgo = 7;
          break;
        case '30days':
          daysAgo = 30;
          break;
        case '90days':
          daysAgo = 90;
          break;
        default:
          console.warn('Unknown timeframe, defaulting to 7 days:', timeframe);
          daysAgo = 7;
      }
      
      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() - daysAgo);
      const startTimestamp = Timestamp.fromDate(startDate);
      const endTimestamp = Timestamp.fromDate(now);

      // Query detection_events collection
      // We need to match by:
      // 1. biologicalEntityId == diseaseId
      // 2. locationId == locationId (or departmentId, roomId if locationId is not directly available)
      // 3. detectionDate >= startDate AND detectionDate <= endDate
      // 4. detectionStatus == 'Confirmed' (only count confirmed cases)
      
      const detectionEventsRef = collection(db, 'detection_events');
      
      // Build query: filter by biologicalEntityId and detectionDate
      // Note: Firestore requires composite indexes for multiple where clauses
      // We'll query by biologicalEntityId and detectionDate, then filter by location in memory
      // This is acceptable for outbreak detection as the dataset per disease is typically small
      let q = query(
        detectionEventsRef,
        where('biologicalEntityId', '==', diseaseId),
        where('detectionDate', '>=', startTimestamp),
        where('detectionDate', '<=', endTimestamp),
        where('detectionStatus', '==', 'Confirmed')
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return 0;
      }

      // Filter by location in memory
      // locationId can match departmentId, roomId, or the locationId field itself
      let count = 0;
      snapshot.docs.forEach(doc => {
        const event = doc.data();
        
        // Check if event matches location
        // Support multiple location matching strategies:
        // 1. Direct locationId match
        // 2. departmentId match (if locationId is a department)
        // 3. roomId match (if locationId is a room)
        const matchesLocation = 
          event.locationId === locationId ||
          event.departmentId === locationId ||
          event.roomId === locationId ||
          event.locationId?.includes(locationId); // Fuzzy match for hierarchical locations
        
        if (matchesLocation) {
          count++;
        }
      });

      return count;
    } catch (error) {
      console.error('Error counting disease occurrences:', error);
      // If query fails (e.g., missing index), return 0 and log warning
      // This prevents RPN calculation from failing, but outbreak detection won't work
      console.warn('Disease occurrence counting failed, returning 0. Check Firestore indexes.');
      return 0;
    }
  }

  /**
   * Trigger EU outbreak alert
   */
  async triggerEUOutbreakAlert(locationId, disease) {
    try {
      const { riskAlertManager } = await import('./riskAlertManager');
      
      const alert = {
        type: 'EU_OUTBREAK',
        severity: 'CRITICAL',
        diseaseId: disease.id,
        diseaseName: disease.commonName || disease.scientificName,
        euDiseaseCode: disease.euDiseaseCode,
        ecdcCode: disease.ecdcCode,
        locationId,
        reportingDeadline: disease.euReportingDeadline,
        ecdcNotificationRequired: true,
        isolationRequired: disease.euIsolationRequired,
        contactTracingRequired: disease.euContactTracingRequired,
        message: `Potential outbreak of ${disease.commonName || disease.scientificName} detected. EU reporting required within ${disease.euReportingDeadline}.`,
        recommendedActions: [
          `Immediate isolation of affected patients (Required: ${disease.euIsolationRequired})`,
          `Contact tracing (Required: ${disease.euContactTracingRequired})`,
          `Report to ECDC via TESSy system within ${disease.euReportingDeadline}`,
          `Implement enhanced infection control measures`,
          `Notify public health authorities`
        ],
        createdAt: new Date()
      };
      
      // Use existing alert manager to create alert
      await riskAlertManager.createAlert(alert);
    } catch (error) {
      console.error('Error triggering EU outbreak alert:', error);
    }
  }
  
  /**
   * Determine risk level from final score
   * Based on FMEA-style RPN thresholds
   */
  determineRiskLevel(score) {
    if (score >= 1000) return 'CRITICAL';
    if (score >= 500) return 'HIGH';
    if (score >= 200) return 'MEDIUM';
    if (score >= 100) return 'LOW';
    return 'MINIMAL';
  }
  
  /**
   * Get risk level color for UI display
   */
  getRiskLevelColor(riskLevel) {
    const colors = {
      'CRITICAL': '#c62828',
      'HIGH': '#f44336',
      'MEDIUM': '#ff9800',
      'LOW': '#ffc107',
      'MINIMAL': '#4caf50'
    };
    return colors[riskLevel] || colors.MINIMAL;
  }
  
  /**
   * Generate actionable recommendations based on risk assessment
   */
  generateRecommendations(finalScore, riskLevel, context, appliedFactors) {
    const recommendations = [];
    
    // Critical risk recommendations
    if (riskLevel === 'CRITICAL') {
      recommendations.push({
        type: 'IMMEDIATE',
        priority: 'CRITICAL',
        action: 'Implement enhanced isolation protocols immediately',
        rationale: `Risk score ${finalScore} exceeds critical threshold (1000)`,
        icon: '🚨'
      });
      
      recommendations.push({
        type: 'IMMEDIATE',
        priority: 'CRITICAL',
        action: 'Notify Infection Control Team and Hospital Administration',
        rationale: 'Critical risk requires immediate escalation',
        icon: '📢'
      });
    }
    
    // High risk recommendations
    if (riskLevel === 'HIGH' || riskLevel === 'CRITICAL') {
      recommendations.push({
        type: 'PROTOCOL',
        priority: 'HIGH',
        action: 'Activate enhanced PPE requirements for all staff',
        rationale: 'High risk environment requires maximum protection',
        icon: '🦺'
      });
      
      recommendations.push({
        type: 'MONITORING',
        priority: 'HIGH',
        action: 'Increase monitoring frequency and documentation',
        rationale: 'High-risk situations require enhanced surveillance',
        icon: '👁️'
      });
    }
    
    // Patient-specific recommendations
    if (context.patient && appliedFactors.patient > 2.0) {
      recommendations.push({
        type: 'PATIENT',
        priority: 'HIGH',
        action: 'Consider patient transfer to specialized unit or enhanced isolation',
        rationale: `Patient has high risk multiplier (×${appliedFactors.patient.toFixed(1)})`,
        icon: '🏥'
      });
      
      recommendations.push({
        type: 'PATIENT',
        priority: 'MEDIUM',
        action: 'Implement patient-specific risk mitigation protocols',
        rationale: 'Multiple patient risk factors identified',
        icon: '📋'
      });
    }
    
    // Environmental recommendations
    if (appliedFactors.environmental > 1.5) {
      recommendations.push({
        type: 'ENVIRONMENTAL',
        priority: 'MEDIUM',
        action: 'Address identified environmental risk factors',
        rationale: `Environmental conditions increase risk by ${((appliedFactors.environmental - 1) * 100).toFixed(0)}%`,
        icon: '🌍'
      });
      
      if (context.environmentalConditions?.activeFactorIds) {
        const factorCount = context.environmentalConditions.activeFactorIds.length;
        if (factorCount >= 3) {
          recommendations.push({
            type: 'ENVIRONMENTAL',
            priority: 'HIGH',
            action: `Urgent: Address ${factorCount} environmental risk factors`,
            rationale: 'Multiple environmental issues compound infection risk',
            icon: '⚠️'
          });
        }
      }
    }
    
    // Biological entity recommendations
    if (appliedFactors.biological > 1.0) {
      recommendations.push({
        type: 'BIOLOGICAL',
        priority: 'MEDIUM',
        action: 'Implement organism-specific control measures',
        rationale: 'Specific biological entities associated with this risk',
        icon: '🦠'
      });
    }
    
    // Location recommendations
    if (appliedFactors.location > 1.5) {
      recommendations.push({
        type: 'LOCATION',
        priority: 'MEDIUM',
        action: 'Review location-specific protocols and controls',
        rationale: 'Location characteristics increase infection risk',
        icon: '📍'
      });
    }
    
    // Medium risk recommendations
    if (riskLevel === 'MEDIUM') {
      recommendations.push({
        type: 'STANDARD',
        priority: 'MEDIUM',
        action: 'Maintain standard infection control protocols',
        rationale: 'Moderate risk level - standard precautions sufficient',
        icon: '✅'
      });
    }
    
    // Low risk recommendations
    if (riskLevel === 'LOW' || riskLevel === 'MINIMAL') {
      recommendations.push({
        type: 'STANDARD',
        priority: 'LOW',
        action: 'Continue routine infection prevention practices',
        rationale: 'Risk level is acceptable with current controls',
        icon: '✓'
      });
    }
    
    return recommendations;
  }
  
  /**
   * Calculate risk assessment for a task
   * Combines protocol risks with task context
   */
  async calculateTaskRisk(task, taskContext = {}) {
    try {
      // Import riskService here to avoid circular dependency
      const { riskService } = await import('./riskService');
      
      // Get all protocol risks for this task
      const allRisks = [];
      
      for (const protocol of task.protocols || []) {
        if (protocol.protocolId) {
          const protocolRisks = await riskService.getProtocolRisks(protocol.protocolId);
          allRisks.push(...protocolRisks);
        }
      }
      
      if (allRisks.length === 0) {
        return {
          baseRPN: 0,
          finalScore: 0,
          riskLevel: 'MINIMAL',
          factors: { location: 1.0, patient: 1.0, environmental: 1.0, biological: 1.0 },
          recommendations: [],
          message: 'No risks associated with this task'
        };
      }
      
      // Calculate comprehensive risk for each protocol risk
      const assessments = await Promise.all(
        allRisks.map(risk => this.calculateComprehensiveRisk(risk, {
          ...taskContext,
          patient: task.context?.patient || taskContext.patient,
          department: task.context?.department || taskContext.department,
          room: task.context?.room || taskContext.room,
          environmentalConditions: task.context?.environmentalConditions || taskContext.environmentalConditions
        }))
      );
      
      // Aggregate results - use highest risk score
      const highestRisk = assessments.reduce((max, current) => 
        current.finalScore > max.finalScore ? current : max
      , assessments[0]);
      
      // Combine all recommendations
      const allRecommendations = assessments.flatMap(a => a.recommendations);
      
      // Deduplicate recommendations by action text
      const uniqueRecommendations = Array.from(
        new Map(allRecommendations.map(rec => [rec.action, rec])).values()
      );
      
      return {
        ...highestRisk,
        totalRisks: assessments.length,
        assessments: assessments,
        recommendations: uniqueRecommendations
      };
      
    } catch (error) {
      console.error('Error calculating task risk:', error);
      throw error;
    }
  }
  
  /**
   * Quick risk assessment for display purposes
   * Simpler calculation without full database lookups
   */
  quickRiskAssessment(baseRPN, patientFactorCount = 0, environmentalFactorCount = 0) {
    // Estimate multipliers based on factor counts
    let patientMultiplier = 1.0;
    if (patientFactorCount > 0) {
      // Assume average multiplier of 2.0 per factor
      patientMultiplier = Math.min(Math.pow(2.0, patientFactorCount), 3.0);
    }
    
    let envMultiplier = 1.0;
    if (environmentalFactorCount > 0) {
      // Assume average multiplier of 1.5 per factor
      envMultiplier = Math.min(Math.pow(1.5, environmentalFactorCount), 2.0);
    }
    
    const finalScore = Math.round(baseRPN * patientMultiplier * envMultiplier);
    const riskLevel = this.determineRiskLevel(finalScore);
    
    return {
      baseRPN,
      finalScore,
      riskLevel,
      estimatedMultipliers: {
        patient: patientMultiplier,
        environmental: envMultiplier
      }
    };
  }
  
  /**
   * Calculate overall risk for a protocol (without specific context)
   * Used for protocol-level risk scoring
   */
  async calculateProtocolRisk(protocolId) {
    try {
      const { riskService } = await import('./riskService');
      const { protocolService } = await import('./protocolService');
      
      const protocol = await protocolService.getProtocol(protocolId);
      const risks = await riskService.getProtocolRisks(protocolId);
      
      if (risks.length === 0) {
        return {
          protocolId,
          protocolName: protocol?.name,
          totalRisks: 0,
          totalRPN: 0,
          averageRPN: 0,
          highestRPN: 0,
          riskLevel: 'MINIMAL'
        };
      }
      
      const totalRPN = risks.reduce((sum, risk) => sum + risk.rpn, 0);
      const averageRPN = Math.round(totalRPN / risks.length);
      const highestRPN = Math.max(...risks.map(r => r.rpn));
      const riskLevel = this.determineRiskLevel(highestRPN);
      
      return {
        protocolId,
        protocolName: protocol?.name,
        totalRisks: risks.length,
        totalRPN,
        averageRPN,
        highestRPN,
        riskLevel,
        risks: risks.map(r => ({
          id: r.id,
          riskScenario: r.riskScenario,
          rpn: r.rpn,
          criticality: r.criticality
        }))
      };
      
    } catch (error) {
      console.error('Error calculating protocol risk:', error);
      throw error;
    }
  }
}

export const riskAssessmentEngine = new RiskAssessmentEngine();

